import React, { useEffect, useState } from 'react';
import { Calendar as CalendarIcon, Settings2, ExternalLink, Info } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import EditDialog from './EditDialog';

/**
 * Extract the calendar id from any pasted URL (embed ?src=..., share ?cid=BASE64...)
 * OR return the string as-is if it looks like an id/email.
 */
const normalizeCalendarSrc = (input) => {
  if (!input) return '';
  const trimmed = input.trim();
  try {
    if (trimmed.startsWith('http')) {
      const url = new URL(trimmed);
      const src = url.searchParams.get('src');
      if (src) return src;
      const cid = url.searchParams.get('cid');
      if (cid) {
        // Base64-decode the cid to get the calendar email
        try {
          let padded = cid;
          while (padded.length % 4) padded += '=';
          const decoded = atob(padded);
          return decoded;
        } catch { /* fall through */ }
      }
    }
  } catch { /* ignore */ }
  if (/%23|%40/.test(trimmed)) return trimmed;
  return encodeURIComponent(trimmed);
};

const buildEmbedUrl = (src, tz) => {
  if (!src) return '';
  const timezone = encodeURIComponent(tz || 'America/Vancouver');
  return `https://calendar.google.com/calendar/embed?src=${src}&ctz=${timezone}&mode=MONTH&showTitle=0&showNav=1&showDate=1&showPrint=0&showTabs=1&showCalendars=0&showTz=0`;
};

/**
 * Best-effort "open in Google Calendar" URL:
 * - Prefer explicit admin-set URL
 * - Otherwise derive using base64(cid) format from the stored src
 */
const derivePublicUrl = (settings) => {
  if (settings.google_calendar_public_url) return settings.google_calendar_public_url;
  if (!settings.google_calendar_src) return '';
  try {
    // Decode any URL-encoded id back to plain email/id, then base64 encode for cid
    const decoded = decodeURIComponent(settings.google_calendar_src);
    // Only encode if it looks like an email/id (contains @ or . )
    if (/[@.]/.test(decoded)) {
      const b64 = btoa(decoded).replace(/=+$/, '');
      return `https://calendar.google.com/calendar/u/0?cid=${b64}`;
    }
  } catch { /* ignore */ }
  return '';
};

const ParishCalendar = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.getSettings();
      setSettings(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (values) => {
    const payload = {
      google_calendar_src: normalizeCalendarSrc(values.google_calendar_src),
      google_calendar_timezone: values.google_calendar_timezone || 'America/Vancouver',
      google_calendar_public_url: (values.google_calendar_public_url || '').trim(),
    };
    const { data } = await api.updateSettings(payload);
    setSettings(data);
    toast({ title: 'Calendar updated', description: 'Public visitors will now see this calendar.' });
  };

  if (!settings) return null;

  const embedUrl = buildEmbedUrl(settings.google_calendar_src, settings.google_calendar_timezone);
  const publicViewUrl = derivePublicUrl(settings);

  return (
    <section className="max-w-6xl mx-auto px-6 pb-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-3">
        <div>
          <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2 flex items-center gap-2">
            <CalendarIcon size={14} /> Upcoming Events
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2a1a45]">Parish Calendar</h2>
          <p className="text-sm text-[#5a534a] mt-2 max-w-xl">
            Masses, meetings, socials, and special liturgies — all in one place. Subscribe to stay up to date.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {publicViewUrl && (
            <a
              href={publicViewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75]"
            >
              Open in Google Calendar <ExternalLink size={13} />
            </a>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-[#3a2359] text-[#3a2359] text-xs uppercase tracking-wider hover:bg-[#3a2359] hover:text-white hover:border-solid"
            >
              <Settings2 size={14} /> Configure Calendar
            </button>
          )}
        </div>
      </div>

      {settings.google_calendar_src ? (
        <div className="bg-white border border-[#e9e0cc] p-2 sm:p-3">
          <iframe
            src={embedUrl}
            title="All Saints Catholic Parish Calendar"
            className="w-full h-[600px] md:h-[720px] border-0"
            frameBorder="0"
            scrolling="no"
          />
        </div>
      ) : (
        <div className="bg-[#f2ead6]/50 border border-dashed border-[#e9e0cc] p-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-white text-[#3a2359] flex items-center justify-center mb-4">
            <Info size={20} />
          </div>
          <h3 className="font-display text-xl text-[#2a1a45] mb-2">Calendar not yet configured</h3>
          <p className="text-sm text-[#5a534a] max-w-md mx-auto">
            An administrator can paste the parish's Google Calendar URL to make it visible here.
          </p>
        </div>
      )}

      <EditDialog
        open={editing}
        title="Configure Parish Calendar"
        fields={[
          {
            name: 'google_calendar_src',
            label: 'Calendar ID or Embed/Share URL',
            required: true,
            placeholder: 'e.g. parish@gmail.com  OR  a share/embed URL from Google Calendar',
          },
          {
            name: 'google_calendar_timezone',
            label: 'Timezone',
            placeholder: 'America/Vancouver',
          },
          {
            name: 'google_calendar_public_url',
            label: '"Open in Google Calendar" link (optional)',
            placeholder: 'Paste the parish\'s public calendar share link here',
          },
        ]}
        initial={{
          google_calendar_src: settings.google_calendar_src ? decodeURIComponent(settings.google_calendar_src) : '',
          google_calendar_timezone: settings.google_calendar_timezone || 'America/Vancouver',
          google_calendar_public_url: settings.google_calendar_public_url || '',
        }}
        onClose={() => setEditing(false)}
        onSubmit={handleSave}
        submitLabel="Save Calendar"
      />
    </section>
  );
};

export default ParishCalendar;
